import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from "styled-components";

import { Button, Divider, Form, Input, Table, Tooltip, notification } from 'antd'
import { SearchOutlined } from '@ant-design/icons'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faPlus } from '@fortawesome/free-solid-svg-icons'

import { SharedTable } from '../../../shared/table'
import { SearchForm } from '../../../shared/search-form'
import { ImageCatalogue } from '../../../shared/cms/image-catalogue'
import { CreatePageDrawer } from '../../../shared/oplevalt/cms/create-page-drawer'

import * as CmsImageService from '../../../../services/bb-api/oplevalt/cms/image'
import * as CmsPageService from '../../../../services/bb-api/oplevalt/cms/page'
import type { Page } from '../../../../services/bb-api/oplevalt/cms/page'

import { txt } from '../../../../locales/i18n'
import { formatDate } from '../../../../utils/date'
import { faFolderImage } from '@fortawesome/pro-duotone-svg-icons'

import { theme } from "../context";

const initialValues = {
	amount: 200,
	category: 'all',
}

export const CmsPages = () => {
	const [pages, setPages] = useState<Page[]>([])
	const [isLoading, setIsLoading] = useState(true)
	const [showCreateDrawer, setShowCreateDrawer] = useState(false)
	const [openImageCatalogue, setOpenImageCatalogue] = useState(false)
	const history = useHistory()

	const getPages = useCallback(async (values: any = null) => {
		setIsLoading(true)

		try {
			const pages = await CmsPageService.query(values)
			setPages(pages)
		} catch (error) {
			const message = (error as Error).message
			notification.error({ message })
		}

		setIsLoading(false)
	}, [])

	const renderCheckmark = (value: any) => {
		if (Boolean(value) === false) return null
		return <FontAwesomeIcon icon={faCheck} />
	}

	const renderLink = (url: any) => {
		return (
			<a
				onClick={({ stopPropagation }) => stopPropagation()}
				href={'https://www.oplevalt.dk/' + url}
				target="_black"
				rel="noreferrer"
			>
				{url}
			</a>
		)
	}

	useEffect(() => {
		getPages()
	}, [getPages])

	const handleSort = (key: string) => {
		function compare(a: any, b: any): -1 | 0 | 1 {
			let valueA: any = a[key]
			let valueB: any = b[key]

			if (Number.isNaN(valueA) && Number.isNaN(valueB)) {
				valueA = valueA.toLowerCase()
				valueB = valueB.toLowerCase()
			}

			if (valueA < valueB) return -1
			if (valueA > valueB) return 1
			return 0
		}

		return { compare }
	}

	return (
		<>
			<MainStyled>
				<section className="search-form">
					<SearchForm initialValues={initialValues} onFinish={getPages}>
						<Button type="primary" onClick={() => setShowCreateDrawer(true)}>
							<FontAwesomeIcon icon={faPlus} />
						</Button>

						<Divider type="vertical" />

						<Tooltip title={txt('global.search')}>
							<Form.Item name="search">
								<Input allowClear placeholder={txt('global.search')} />
							</Form.Item>
						</Tooltip>

						<Form.Item>
							<Button type="primary" htmlType="submit">
								{txt('global.search')} <SearchOutlined />
							</Button>
						</Form.Item>

						<Tooltip title={txt('global.images')}>
							<Button type="link" style={{ marginLeft: 'auto' }} onClick={() => setOpenImageCatalogue(true)}>
								<FontAwesomeIcon icon={faFolderImage} />
							</Button>
						</Tooltip>
					</SearchForm>
				</section>

				<section>
					<SharedTable
						loading={isLoading}
						dataSource={pages}
						rowKey="id"
						onRow={(page) => ({
							onClick: () => history.push('/oplevalt/cms/' + page.id),
						})}
					>
						<Table.Column dataIndex="title" title={txt('cms.title')} width="18ch" ellipsis />
						<Table.Column
							dataIndex="published"
							title={txt('cms.published')}
							render={renderCheckmark}
							width="10ch"
							align="center"
						/>
						<Table.Column
							dataIndex="url"
							title="URL"
							width="18ch"
							ellipsis
							render={renderLink}
							sorter={handleSort('url')}
							sortDirections={['descend', 'ascend']}
						/>
						<Table.Column dataIndex="created" title={txt('cms.created')} render={formatDate} width="28ch" ellipsis />
						<Table.Column dataIndex="changed" title={txt('cms.updated')} render={formatDate} width="28ch" ellipsis />
						<Table.Column dataIndex="metaDescription" title={txt('cms.metaDescription')} width="32ch" ellipsis />
					</SharedTable>
				</section>
			</MainStyled>

			<CreatePageDrawer
				visible={showCreateDrawer}
				onClose={() => setShowCreateDrawer(false)}
				onCreate={async (page) => {
					const pageId = await CmsPageService.create(page)
					history.push('/oplevalt/cms/' + pageId)
				}}
			/>

			<ImageCatalogue
				useVisible={[openImageCatalogue, setOpenImageCatalogue]}
				sourcePrefix="https://filer.oplevalt.dk/cms/large_"
				onQuery={CmsImageService.query}
				onCreate={CmsImageService.create}
				onRemove={CmsImageService.remove}
				showTransparent
			/>
		</>
	)
}

const MainStyled = styled.main`
	background-color: ${theme.orange.neutral}; 

	.search-form {
		background-color: white;
	}
`